/**
 * What does not hang together.
 *
 * These are model problems, not tool failures - an unresolved reference is
 * something for a person to decide about - so they are grouped by check and
 * each one links to the thing it is about.
 *
 * Since issue #51 each group says what the check MEANS and what to do about it,
 * and the groups are ordered worst first. It used to show the check's internal
 * name and nothing else, in whatever order the server emitted them: a single
 * source loop - the only error a model can have here - sat wherever it fell
 * among fifty warnings, under a label only the code explained.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../api'
import type { Finding } from '../schema'
import { MONOSPACE } from '../theme'
import EmptyState from '../components/common/EmptyState'
import { SEVERITIES, findingAnchor, findingHref, groupFindings, severityCounts } from './findings-model'

const SEVERITY_COLOUR: Record<string, 'error' | 'warning' | 'info'> = {
  error: 'error',
  warning: 'warning',
  info: 'info',
}

/** What each severity is, said once above the list rather than left to the colour. */
const SEVERITY_MEANING: Record<string, string> = {
  error: 'the model contradicts itself',
  warning: 'something is missing or does not resolve',
  info: 'declared, and nothing refers to it yet',
}

/** Where a finding was written: the file's name and the line. */
function where(finding: Finding): string {
  const file = finding.path ? finding.path.replace(/\\/g, '/').split('/').pop() : ''
  if (file && finding.line) return `${file}:${finding.line}`
  return file || (finding.line ? `line ${finding.line}` : '')
}

export default function Findings() {
  const [findings, setFindings] = useState<Finding[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [severity, setSeverity] = useState<string>('all')

  useEffect(() => {
    api
      .findings()
      .then((page) => setFindings(page.items))
      .catch((failure: ApiError) => setError(failure))
  }, [])

  const counts = useMemo(() => severityCounts(findings ?? []), [findings])
  const groups = useMemo(
    () => groupFindings(
      (findings ?? []).filter((finding) => severity === 'all' || finding.severity === severity),
    ),
    [findings, severity],
  )

  // Arriving with a hash (the dashboard's Orphans card links to #finding-orphan,
  // issue #72) scrolls that group into view. The browser's own anchor jump runs
  // at navigation, before the fetch has finished and the group exists, so it
  // misses; this runs once the groups are rendered. Once per hash: it must not
  // drag the page back up when the reader then changes the severity filter.
  const { hash } = useLocation()
  const scrolledTo = useRef('')
  useEffect(() => {
    if (!hash || scrolledTo.current === hash) return
    const target = document.getElementById(hash.slice(1))
    if (!target) return
    target.scrollIntoView()
    scrolledTo.current = hash
  }, [hash, groups])

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!findings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">Findings</Typography>
        <Typography variant="body2" color="text.secondary">
          Consistency checks over the whole model. These are things to decide about, not errors
          in the tool.
        </Typography>
      </Box>

      {findings.length === 0 ? (
        <EmptyState title="Nothing inconsistent found" description="Every check passed." />
      ) : (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={severity}
            onChange={(_event, next) => next && setSeverity(next)}
            aria-label="Filter by severity"
          >
            <ToggleButton value="all">All ({findings.length})</ToggleButton>
            {SEVERITIES.filter((name) => counts[name]).map((name) => (
              <ToggleButton key={name} value={name}>
                {name} ({counts[name]})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {SEVERITIES.filter((name) => counts[name])
              .map((name) => `${name}: ${SEVERITY_MEANING[name]}`)
              .join(' · ')}
          </Typography>

          {groups.map((group) => (
            <Paper
              key={group.check}
              id={findingAnchor(group.check)}
              sx={{ p: 1.5 }}
              aria-label={group.info.label}
              component="section"
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label={group.severity}
                  size="small"
                  color={SEVERITY_COLOUR[group.severity] ?? 'default'}
                  sx={{ height: 20, minWidth: 68 }}
                />
                <Typography variant="subtitle1" component="h2">
                  {group.info.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.findings.length}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {/* The server's own name for it, for searching the docs or a log. */}
                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: MONOSPACE }}>
                  {group.check}
                </Typography>
              </Stack>
              {group.info.help && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1, maxWidth: '90ch' }}>
                  {group.info.help}
                </Typography>
              )}

              <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                {group.findings.map((finding, index) => (
                  <Box key={`${finding.element}-${index}`} sx={{ py: 0.75 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                      {findingHref(finding) ? (
                        <RouterLink
                          to={findingHref(finding) as string}
                          style={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}
                        >
                          {finding.element}
                        </RouterLink>
                      ) : (
                        <Typography component="span" sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>
                          {finding.element}
                        </Typography>
                      )}
                      {where(finding) && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: MONOSPACE }}>
                          {where(finding)}
                        </Typography>
                      )}
                    </Stack>
                    <Typography variant="body2">{finding.message}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))}
        </>
      )}
    </Stack>
  )
}
